// TKOS icon renderer.
//
// Returns an SVGElement that consumes .tkos-icon styles. Use this from any
// component that needs an icon — do not inline raw SVG markup, and never
// import an external icon library.

import './icons.css';
import { iconPath, type IconName } from './paths';

export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type IconTone =
  | 'inherit'
  | 'accent'
  | 'muted'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info';

export interface IconOptions {
  size?: IconSize;
  tone?: IconTone;
  className?: string;
  /** Accessible name. Omit for purely decorative icons. */
  title?: string;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

export function renderIcon(name: IconName, opts: IconOptions = {}): SVGElement {
  const { size = 'md', tone = 'inherit', className = '', title } = opts;

  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('aria-hidden', title ? 'false' : 'true');
  if (title) {
    svg.setAttribute('role', 'img');
    const t = document.createElementNS(SVG_NS, 'title');
    t.textContent = title;
    svg.appendChild(t);
  }

  const classes = ['tkos-icon', `tkos-icon--${size}`];
  if (tone !== 'inherit') classes.push(`tkos-icon--${tone}`);
  if (className) classes.push(className);
  svg.setAttribute('class', classes.join(' '));

  // The path source is authored without an <svg> wrapper. innerHTML on an
  // SVGElement is supported in every modern browser; we own the source.
  svg.innerHTML = iconPath(name);

  return svg;
}

/**
 * Inline SVG markup form — for cases where you need a string (server-render,
 * template literals, dangerouslySetInnerHTML-style attaches). Prefer
 * renderIcon() everywhere DOM access is possible.
 */
export function iconMarkup(name: IconName, opts: IconOptions = {}): string {
  const { size = 'md', tone = 'inherit', className = '', title } = opts;
  const classes = ['tkos-icon', `tkos-icon--${size}`];
  if (tone !== 'inherit') classes.push(`tkos-icon--${tone}`);
  if (className) classes.push(className);

  const titleEl = title
    ? `<title>${escapeXml(title)}</title>`
    : '';
  const ariaHidden = title ? 'false' : 'true';
  const role = title ? ' role="img"' : '';

  return (
    `<svg xmlns="${SVG_NS}" viewBox="0 0 24 24" class="${classes.join(' ')}"` +
    ` aria-hidden="${ariaHidden}"${role}>${titleEl}${iconPath(name)}</svg>`
  );
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export type { IconName } from './paths';
export { listIcons } from './paths';
